import { faArrowAltCircleLeft, faEyeDropper } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ColorPicker from "../../../Component/ColorPicker/ColorPicker";
import Modal from "../../../Component/Modal/Modal";
import IframeCodeCatalogue from "./IframeCodeCatalogue";

const CreateWidgetCatalogue = ({ userId }) => {
  const user = useSelector((state) => state.userReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [widgetId, setWidgetId] = useState("");
  const [widgetData, setWidgetData] = useState({
    userId: userId,
    title: "",
    priceActive: "true",
    isOnline: "online",
    type: "Widget Catalogue",
    btnColor: "",
    backgroundColor: "",
    labelColor: "",
    borderColor: "",
    contactMail: "",
    contactPhone: "",
  });
  const [categories, setCategories] = useState([
    {
      name: "",
      prestations: [
        { name: "", description: "", price: 0, duration: 0, quantityEnabled: false },
      ],
    },
  ]);

  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickers, setColorPickers] = useState({
    backgroundColor: false,
    btnColor: false,
    labelColor: false,
    borderColor: false,
  });

  const handleInputChange = (e) => {
    const value =
      e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setWidgetData({ ...widgetData, [e.target.name]: value });
  };

  const handleColorPickerToggle = (fieldName) => {
    setColorPickers((prevPickers) => ({
      ...Object.keys(prevPickers).reduce((acc, key) => {
        acc[key] = key === fieldName ? !prevPickers[key] : false;
        return acc;
      }, {}),
    }));
    setActiveColorPicker(fieldName);
  };

  const handleColorChange = (color) => {
    setWidgetData((prevData) => ({
      ...prevData,
      [activeColorPicker]: color.hex,
    }));
  };

  // ── Category management ──

  const addCategory = () => {
    setCategories([
      ...categories,
      {
        name: "",
        prestations: [
          { name: "", description: "", price: 0, duration: 0, quantityEnabled: false },
        ],
      },
    ]);
  };

  const removeCategory = (catIdx) => {
    if (categories.length <= 1) return;
    setCategories(categories.filter((_, i) => i !== catIdx));
  };

  const updateCategoryName = (catIdx, name) => {
    setCategories(
      categories.map((cat, i) => (i === catIdx ? { ...cat, name } : cat))
    );
  };

  // ── Prestation management ──

  const addPrestation = (catIdx) => {
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? {
              ...cat,
              prestations: [
                ...cat.prestations,
                { name: "", description: "", price: 0, duration: 0, quantityEnabled: false },
              ],
            }
          : cat
      )
    );
  };

  const removePrestation = (catIdx, prestaIdx) => {
    if (categories[catIdx].prestations.length <= 1) return;
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? {
              ...cat,
              prestations: cat.prestations.filter((_, pi) => pi !== prestaIdx),
            }
          : cat
      )
    );
  };

  const updatePrestation = (catIdx, prestaIdx, field, value) => {
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? {
              ...cat,
              prestations: cat.prestations.map((presta, pi) =>
                pi === prestaIdx ? { ...presta, [field]: value } : presta
              ),
            }
          : cat
      )
    );
  };

  // ── Submit ──

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const categoriesWithOrder = categories.map((cat, catIdx) => ({
      ...cat,
      order: catIdx,
      prestations: cat.prestations.map((presta, prestaIdx) => ({
        ...presta,
        order: prestaIdx,
      })),
    }));

    const payload = {
      ...widgetData,
      priceActive: widgetData.priceActive === "true",
      categories: categoriesWithOrder,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/catalogue/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success("Widget créé avec succès");
        setWidgetId(data.widget._id);
      } else {
        switch (response.status) {
          case 401:
            toast.error("Vous n'êtes pas connecté");
            break;
          case 400:
            toast.error("Attention, un widget existe déjà avec ce nom.");
            break;
          default:
            toast.error("Une erreur est survenue");
            break;
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    "text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";

  return (
    <div className="p-4 w-full">
      <Link to={`/`}>
        <button className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 my-1 w-[200px] flex justify-center items-center gap-2 text-sm font-medium transition-colors">
          <FontAwesomeIcon icon={faArrowAltCircleLeft} />
          <p>Revenir à la liste</p>
        </button>
      </Link>

      {user.user.isPremium ? (
        <div className="max-w-2xl mx-auto mt-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* ── Section 1 : Informations générales ── */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Informations générales
              </p>

              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Titre du widget *
                </label>
                <input
                  className={inputClass}
                  type="text"
                  name="title"
                  placeholder="Ex : Salon Marie — Tarifs 2025"
                  value={widgetData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Afficher les prix au client ?
                  </label>
                  <select
                    className={inputClass}
                    name="priceActive"
                    value={widgetData.priceActive}
                    onChange={handleInputChange}
                  >
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Statut
                  </label>
                  <select
                    className={inputClass}
                    name="isOnline"
                    value={widgetData.isOnline}
                    onChange={handleInputChange}
                  >
                    <option value="online">En ligne</option>
                    <option value="offline">Hors ligne</option>
                  </select>
                </div>
              </div>
            </div>

            {/* ── Section 2 : Catalogue de prestations ── */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Catalogue de prestations
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong className="text-amber-900">Organisez vos prestations par catégories.</strong>{" "}
                  Chaque prestation peut avoir un nom, une description, un prix, une durée estimée, et une option de quantité.
                </p>
              </div>

              {categories.map((cat, catIdx) => (
                <div
                  key={catIdx}
                  className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-3"
                >
                  {/* Category header */}
                  <div className="flex items-center gap-2">
                    <input
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                      type="text"
                      placeholder="Nom de la catégorie"
                      value={cat.name}
                      onChange={(e) => updateCategoryName(catIdx, e.target.value)}
                    />
                    {categories.length > 1 && (
                      <button
                        type="button"
                        className="w-8 h-8 shrink-0 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 flex items-center justify-center transition-colors"
                        onClick={() => removeCategory(catIdx)}
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Column headers */}
                  <div className="flex items-center gap-2 px-1">
                    <span className="flex-1 text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                      Nom
                    </span>
                    <span className="w-[140px] text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                      Description
                    </span>
                    <span className="w-[65px] text-[9px] font-bold text-gray-400 uppercase tracking-wider text-right">
                      Prix
                    </span>
                    <span className="w-[55px] text-[9px] font-bold text-gray-400 uppercase tracking-wider text-center">
                      Durée
                    </span>
                    <span className="w-[50px] text-[9px] font-bold text-gray-400 uppercase tracking-wider text-center">
                      Qté
                    </span>
                    <span className="w-[26px]" />
                  </div>

                  {/* Prestation rows */}
                  {cat.prestations.map((presta, prestaIdx) => (
                    <div
                      key={prestaIdx}
                      className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
                    >
                      <input
                        className="flex-1 min-w-0 rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white transition-all"
                        type="text"
                        placeholder="Nom"
                        value={presta.name}
                        onChange={(e) =>
                          updatePrestation(catIdx, prestaIdx, "name", e.target.value)
                        }
                      />
                      <input
                        className="w-[140px] rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white transition-all"
                        type="text"
                        placeholder="Description"
                        value={presta.description}
                        onChange={(e) =>
                          updatePrestation(catIdx, prestaIdx, "description", e.target.value)
                        }
                      />
                      <input
                        className="w-[65px] rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white transition-all text-right"
                        type="number"
                        placeholder="€"
                        value={presta.price}
                        onChange={(e) =>
                          updatePrestation(catIdx, prestaIdx, "price", Number(e.target.value))
                        }
                      />
                      <input
                        className="w-[55px] rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white transition-all text-center"
                        type="number"
                        placeholder="min"
                        value={presta.duration}
                        onChange={(e) =>
                          updatePrestation(catIdx, prestaIdx, "duration", Number(e.target.value))
                        }
                      />
                      <div className="w-[50px] flex items-center justify-center gap-1 shrink-0">
                        <input
                          type="checkbox"
                          id={`qty-${catIdx}-${prestaIdx}`}
                          className="w-3.5 h-3.5 accent-gray-900"
                          checked={presta.quantityEnabled}
                          onChange={(e) =>
                            updatePrestation(catIdx, prestaIdx, "quantityEnabled", e.target.checked)
                          }
                        />
                        <label
                          htmlFor={`qty-${catIdx}-${prestaIdx}`}
                          className="text-[10px] text-gray-500 whitespace-nowrap cursor-pointer"
                        >
                          Qté
                        </label>
                      </div>
                      {cat.prestations.length > 1 && (
                        <button
                          type="button"
                          className="w-[26px] h-[26px] shrink-0 rounded-md bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                          onClick={() => removePrestation(catIdx, prestaIdx)}
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                      {cat.prestations.length <= 1 && (
                        <span className="w-[26px] shrink-0" />
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    className="w-full mt-1 p-2 rounded-lg text-xs font-semibold border-2 border-dashed border-gray-200 text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-all"
                    onClick={() => addPrestation(catIdx)}
                  >
                    + Ajouter une prestation
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="w-full p-3 rounded-xl text-sm font-semibold border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all"
                onClick={addCategory}
              >
                + Ajouter une catégorie
              </button>
            </div>

            {/* ── Section 3 : Apparence ── */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Apparence
              </p>

              <div className="-mx-3 flex flex-wrap">
                <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                  <ColorPicker
                    label="Couleur de fond"
                    color={widgetData.backgroundColor}
                    onColorChange={(color) => handleColorChange(color)}
                    isOpen={colorPickers.backgroundColor}
                    togglePicker={() => handleColorPickerToggle("backgroundColor")}
                    placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                  />
                </div>
                <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                  <ColorPicker
                    label="Couleur des boutons"
                    color={widgetData.btnColor}
                    onColorChange={(color) => handleColorChange(color)}
                    isOpen={colorPickers.btnColor}
                    togglePicker={() => handleColorPickerToggle("btnColor")}
                    placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                  />
                </div>
                <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                  <ColorPicker
                    label="Couleur du texte"
                    color={widgetData.labelColor}
                    onColorChange={(color) => handleColorChange(color)}
                    isOpen={colorPickers.labelColor}
                    togglePicker={() => handleColorPickerToggle("labelColor")}
                    placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                  />
                </div>
              </div>
              <div className="-mx-3 flex flex-wrap mt-1">
                <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                  <ColorPicker
                    label="Couleur de bordure"
                    color={widgetData.borderColor}
                    onColorChange={(color) => handleColorChange(color)}
                    isOpen={colorPickers.borderColor}
                    togglePicker={() => handleColorPickerToggle("borderColor")}
                    placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                  />
                </div>
              </div>

              {/* Live preview */}
              <div
                className="rounded-xl p-4 mt-1"
                style={{
                  backgroundColor: widgetData.backgroundColor || "#1a1a1a",
                  border: widgetData.borderColor ? `2px solid ${widgetData.borderColor}` : "none",
                }}
              >
                <p className="text-[9px] font-bold uppercase tracking-widest mb-3"
                  style={{ color: widgetData.labelColor || "#ffffff", opacity: 0.35 }}
                >
                  Aperçu en temps réel
                </p>
                {/* Prestation active */}
                <div
                  className="flex items-center justify-between rounded-lg px-3 py-2 mb-1.5"
                  style={{ backgroundColor: widgetData.btnColor || "#ffffff" }}
                >
                  <span className="text-xs font-medium" style={{ color: widgetData.labelColor || "#ffffff" }}>
                    Prestation exemple
                  </span>
                  <span
                    className="text-[11px] font-bold rounded px-2 py-0.5"
                    style={{
                      color: widgetData.labelColor || "#ffffff",
                      backgroundColor: `${widgetData.labelColor || "#ffffff"}20`,
                    }}
                  >
                    35€
                  </span>
                </div>
                {/* Prestation inactive */}
                <div
                  className="flex items-center justify-between rounded-lg px-3 py-2 mb-1.5"
                  style={{ backgroundColor: `${widgetData.labelColor || "#ffffff"}10` }}
                >
                  <span className="text-xs font-medium" style={{ color: widgetData.labelColor || "#ffffff" }}>
                    Balayage
                  </span>
                  <span className="text-[11px] font-bold rounded px-2 py-0.5"
                    style={{
                      color: widgetData.labelColor || "#ffffff",
                      backgroundColor: `${widgetData.labelColor || "#ffffff"}15`,
                    }}
                  >
                    60€
                  </span>
                </div>
                <div
                  className="flex items-center justify-between rounded-lg px-3 py-2"
                  style={{ backgroundColor: `${widgetData.labelColor || "#ffffff"}10` }}
                >
                  <span className="text-xs font-medium" style={{ color: widgetData.labelColor || "#ffffff" }}>
                    Coloration
                  </span>
                  <span className="text-[11px] font-bold rounded px-2 py-0.5"
                    style={{
                      color: widgetData.labelColor || "#ffffff",
                      backgroundColor: `${widgetData.labelColor || "#ffffff"}15`,
                    }}
                  >
                    80€
                  </span>
                </div>
              </div>
            </div>

            {/* ── Section 4 : Contact ── */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Contact
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Email de réception *
                  </label>
                  <input
                    className={inputClass}
                    type="email"
                    name="contactMail"
                    placeholder="contact@salon-marie.fr"
                    value={widgetData.contactMail}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Téléphone
                  </label>
                  <input
                    className={inputClass}
                    type="tel"
                    name="contactPhone"
                    placeholder="01 23 45 67 89"
                    value={widgetData.contactPhone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* ── Submit ── */}
            <button
              type="submit"
              className="w-full p-3 rounded-xl text-sm font-semibold text-white bg-black hover:bg-gray-800 transition-colors"
            >
              {isLoading ? "Chargement..." : "Créer le widget"}
            </button>
          </form>
        </div>
      ) : null}

      {widgetId && (
        <Modal
          title="Code iframe"
          isOpen={true}
          onClose={() => setWidgetId("")}
          children={<IframeCodeCatalogue widgetId={widgetId} />}
        />
      )}
    </div>
  );
};

export default CreateWidgetCatalogue;
